import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import reduxStore from "reduxstore/reduxStore";
import i18n from "i18n/i18n";
import { OK } from "http-status-codes";
import isEmpty from "lodash/isEmpty";
import debounce from "lodash/debounce";
import { Link } from "react-router-dom";
import { Input, Button, AlertCard } from "@texada/ui";
import fleetlogicAPI from "helpers/fleetlogicAPI";
import {
  APP_DEFAULT_URL,
  TIS_DB_CONNECTION,
  NON_UPGRADED_USER_URL,
  AUTO_DRAFT_UPDATE_INTERVAL
} from "constants/AppConstants";
import { isPlayEnv, getApiErrorMsg } from "helpers/helper";
import MainScreen from "./MainScreen";
import { useUser, useInterval } from "helpers/hooks";
import { isValidEmail } from "helpers/validators";
import {
  LoginSource,
  openSrmManagement,
  getUserStatus,
  LoginTenant
} from "./loginUtils";
import { setCurrentUser } from "actions/authActions";

const TisRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const user = useUser();
  const [email, setEmail] = useState(user?.email || "");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [tisUserId, setTisUserId] = useState(user.tis_user_id);
  const [tenants, setTenants] = useState([]);

  const isForceMigration =
    new Date() >=
    new Date(user?.companyFeatures?.tis_mandatory_migration_cut_off_date);
  const isIdp =
    user?.companySsoConfig?.auth0Configuration?.Auth0ConnectionPrimary
      ?.ConnectionType !== TIS_DB_CONNECTION;

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!submitted || isLoading) return;
      if (!document.hidden) {
        checkEmailVerifyStatus();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, []);

  useEffect(() => {
    getTenantList();
  }, [user]);

  useInterval(() => {
    if (!submitted || isLoading) return;
    return checkEmailVerifyStatus();
  }, AUTO_DRAFT_UPDATE_INTERVAL);

  const checkEmailVerifyStatus = async () => {
    const { email_verified } = await getUserStatus(email);
    if (email_verified !== true) return false;
    setSuccess(i18n.t("your_email_has_been_verified"));
    await reduxStore.dispatch(
      setCurrentUser({
        ...user,
        tis_user_id: tisUserId,
        is_tis: true,
        email,
        email_verified
      })
    );
    onRedirectDebounce();
    return true;
  };

  const getTenantList = async () => {
    const res = await getUserStatus(email);
    setTenants(res?.tenants || []);
    if (res?.migrated && !res?.email_verified) {
      setSubmitted(true);
    }
  };

  const validateEmail = () => {
    let newError = "";
    let newSuccess = "";
    if (isEmpty(email)) {
      newError = i18n.t("field_is_required", { field: i18n.t("email") });
    }

    if (!isValidEmail(email)) {
      newError = i18n.t("invalid_email_address");
    }
    setError(newError);
    setSuccess(newSuccess);
    return isEmpty(newError);
  };

  const preventDefault = callback => e => {
    e.preventDefault();
    callback();
  };

  const onSubmit = async () => {
    setIsLoading(true);
    const isValid = validateEmail();
    if (!isValid) {
      setIsLoading(false);
      return false;
    }

    if (submitted) {
      await resendVerificationEmail();
      return;
    }

    if (isIdp) {
      await enableIdpUser();
      return;
    }
    await inviteTisUser();
  };

  async function enableIdpUser() {
    try {
      const res = await fleetlogicAPI.put(
        `${NON_UPGRADED_USER_URL}/${user.id}/`,
        {
          is_tis: true,
          email
        }
      );

      await reduxStore.dispatch(
        setCurrentUser({
          ...user,
          tis_user_id: res.data.tis_user_id,
          is_tis: true,
          email,
          email_verified: true
        })
      );
      onRedirectDebounce();
    } catch (err) {
      setError(getApiErrorMsg(err));
      setIsLoading(false);
    }
  }

  const inviteTisUser = async () => {
    try {
      const res = await fleetlogicAPI.post(
        `${NON_UPGRADED_USER_URL}/${user.id}/invite`,
        {
          email,
          external_auth: true
        }
      );

      if (res.status === OK || res?.data?.UserId) {
        setTisUserId(res?.data?.UserId || res.data.tis_user_id);
        if (!res.data.hasOwnProperty("UserId")) {
          await checkEmailVerifyStatus();
        }
        setSuccess(
          tenants.length > 1 &&
            tenants.filter(tenant => tenant.title === LoginTenant.LIVE)
              .length === 0
            ? i18n.t(
                "you_may_receive_multiple_verification_emails_per_company_verify_one_would_apply_to_all",
                { companies: tenants.map(tenant => tenant.title).join(", ") }
              )
            : i18n.t("we_have_just_sent_an_verification_email")
        );
        setSubmitted(true);
      } else {
        setError(i18n.t("error_to_migrate_user"));
      }
      setIsLoading(false);
    } catch (err) {
      setError(getApiErrorMsg(err));
      setIsLoading(false);
    }
  };

  async function resendVerificationEmail() {
    try {
      await fleetlogicAPI.post(`${NON_UPGRADED_USER_URL}/${user.id}/verify`, {
        email,
        external_auth: true
      });
      setSuccess(i18n.t("we_have_just_sent_an_verification_email"));
      setIsLoading(false);
    } catch (err) {
      const isVerified = await checkEmailVerifyStatus();
      if (isVerified) {
        return;
      }
      setError(getApiErrorMsg(err));
      setIsLoading(false);
    }
  }

  const redirectPage = async () => {
    setIsLoading(true);
    if (query.get("callbackUrl") === LoginSource.SRM) {
      await openSrmManagement(
        user?.compperms?.operator_by_division,
        query.get("division"),
        query.get("div_loc_id")
      );
      return;
    }
    if (query.get("callbackUrl")) {
      window.location.href = query.get("callbackUrl");
      return;
    }
    navigate(APP_DEFAULT_URL);
  };

  const onRedirectDebounce = debounce(redirectPage, 2000);

  const onSkip = async () => {
    const { email_verified } = await getUserStatus(email);
    if (email_verified !== true && tisUserId) {
      setSuccess("");
      setError(i18n.t("please_verify_your_email_to_continue"));
      setSubmitted(true);
      return;
    }
    redirectPage();
  };

  return (
    <MainScreen>
      <AlertCard type="success" timeout={null} closable={false} size="full">
        {isForceMigration
          ? i18n.t(
              "our_login_process_has_changed_click_the_button_below_to_reset_your_password_and_migrate_to_texada_identity_service_tis"
            )
          : i18n.t(
              "tis_is_here_bringing_with_it_increased_security_and_a_streamlined_login_process_click_the_button_below_to_begin_your_migration"
            )}
        <br />
        <Link
          to="https://help.texadasoftware.com/en/knowledge/1302/about-the-texada-identity-service-rental"
          target="_blank"
          rel="noreferrer noopener"
        >
          {i18n.t("learn_more_about_tis_migration")}
        </Link>
      </AlertCard>
      <form className="login_form" onSubmit={preventDefault(onSubmit)}>
        <Input
          className={`email ${isPlayEnv() ? "play" : ""}`}
          field="email"
          id="email"
          label={i18n.t("update_your_email_address_here")}
          value={email}
          error={error}
          onChange={e => setEmail(e.target.value)}
          inputProps={{ autoCapitalize: "off" }}
          required
          disabled={isLoading}
        />
        {success && <p className="success-message">{success}</p>}
        <Button
          className="login-button"
          isLoading={isLoading}
          buttonProps={{ type: "submit" }}
        >
          {isIdp || !submitted
            ? i18n.t("set_up_now")
            : i18n.t("resend_verification_email")}
        </Button>
        {!isForceMigration && (
          <div className="button-link-container">
            <Link onClick={onSkip} className="login-link">
              {i18n.t("no_skip_it")}
            </Link>
          </div>
        )}
      </form>
    </MainScreen>
  );
};

export default TisRegister;
